import React, { useState, useEffect } from 'react';
import { voucherAPI, branchAPI, planAPI } from '../../services/api';
import { useBranchStore, useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import usePermission from '../../hooks/usePermission';
import './Vouchers.css';
import DateInput from '../../components/DateInput/DateInput';
import { formatDate, toInputDate } from '../../utils/dateFormat';
import { planSummary } from '../../utils/bandwidthFormat';

const Vouchers = () => {
    const { selectedBranch } = useBranchStore();
    const { canCreate, canDelete, canExport } = usePermission('vouchers');
    const canManage = canCreate || canDelete;
    const [vouchers, setVouchers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Delete Confirmation State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [voucherToDelete, setVoucherToDelete] = useState(null);

    const [formData, setFormData] = useState({
        branchId: '',
        planId: '',
        quantity: 10,
        batchName: '',
        validTill: '',
        codeLength: 8,
        maxDevices: 1,
        mode: 'batch',   // 'batch' | 'multi'
        customerName: '',
        customerMobile: '',
        customCode: '',
    });

    useEffect(() => {
        fetchData();
        if (selectedBranch?._id) {
            setFormData(prev => ({ ...prev, branchId: selectedBranch._id }));
            fetchPlans(selectedBranch._id);
        }
    }, [selectedBranch, statusFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch?._id;
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;

            const [vouchersRes, branchesRes] = await Promise.all([
                voucherAPI.getAll(branchId, params),
                branchAPI.getAll(null, { limit: 1000 })
            ]);

            setVouchers(vouchersRes.data.data.vouchers || []);
            setBranches(branchesRes.data.data.branches || []);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const fetchPlans = async (branchId) => {
        try {
            const response = await planAPI.getAll(branchId);
            setPlans(response.data.data.plans || []);
        } catch (error) {
            console.error('Failed to fetch plans');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'branchId' && value) {
            fetchPlans(value);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        const isMulti = formData.mode === 'multi';
        const payload = {
            ...formData,
            quantity: isMulti ? 1 : formData.quantity,
            maxDevices: isMulti ? formData.maxDevices : 1,
        };
        try {
            await voucherAPI.generate(payload);
            toast.success(isMulti
                ? `Multi-device voucher created (${formData.maxDevices} devices)`
                : `Generated ${formData.quantity} vouchers successfully!`);
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to generate vouchers');
        }
    };

    const confirmDelete = async () => {
        if (!voucherToDelete) return;
        try {
            await voucherAPI.delete(voucherToDelete);
            toast.success('Voucher deleted');
            fetchData();
            setShowDeleteModal(false);
            setVoucherToDelete(null);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Cannot delete used voucher');
        }
    };

    const handleDelete = (id) => {
        setVoucherToDelete(id);
        setShowDeleteModal(true);
    };

    const resetForm = () => {
        const branchId = selectedBranch?._id || '';
        setFormData({
            branchId,
            planId: '',
            quantity: 10,
            batchName: '',
            validTill: '',
            codeLength: 8,
            maxDevices: 1,
            mode: 'batch',
            customerName: '',
            customerMobile: '',
            customCode: '',
        });
        if (branchId) fetchPlans(branchId);
    };

    const copyToClipboard = (text) => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text)
                .then(() => toast.success('Copied to clipboard!'))
                .catch(() => fallbackCopy(text));
        } else {
            fallbackCopy(text);
        }
    };

    const fallbackCopy = (text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";  // Avoid scrolling to bottom
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            toast.success('Copied to clipboard!');
        } catch (err) {
            toast.error('Unable to copy to clipboard');
        }
        document.body.removeChild(textArea);
    };

    const groupByBatch = (vouchers) => {
        const batches = {};
        vouchers.forEach(v => {
            if (!batches[v.batchId]) {
                batches[v.batchId] = {
                    batchId: v.batchId,
                    batchName: v.batchName,
                    vouchers: [],
                    total: 0,
                    used: 0,
                    active: 0
                };
            }
            batches[v.batchId].vouchers.push(v);
            batches[v.batchId].total++;
            if (v.status === 'used') batches[v.batchId].used++;
            if (v.status === 'active') batches[v.batchId].active++;
        });
        return Object.values(batches);
    };

    const handlePrintBatch = (batch) => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error('Please allow popups to print vouchers');
            return;
        }

        const vouchersHtml = batch.vouchers.map(v => `
            <div class="voucher-card">
                <div class="header">
                    <h3>WiFi Voucher</h3>
                    <span class="plan">${v.planId?.name || 'Standard Plan'}</span>
                </div>
                <div class="code-box">
                    <code>${v.code}</code>
                </div>
                <div class="details">
                    <p>Valid: ${v.validTill ? formatDate(v.validTill) : 'No Expiry'}</p>
                    <p class="batch">Batch: ${batch.batchName}</p>
                </div>
            </div>
        `).join('');

        const content = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Vouchers - ${batch.batchName}</title>
                <style>
                    body { font-family: sans-serif; padding: 20px; }
                    .voucher-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
                    .voucher-card { 
                        border: 2px dashed #333; 
                        padding: 15px; 
                        border-radius: 8px; 
                        text-align: center; 
                        page-break-inside: avoid;
                        background: white;
                    }
                    .header h3 { margin: 0 0 5px 0; color: #3b82f6; }
                    .plan { font-size: 0.9em; color: #666; font-weight: bold; }
                    .code-box { 
                        margin: 15px 0; 
                        background: #f3f4f6; 
                        padding: 10px; 
                        border-radius: 4px; 
                        border: 1px solid #e5e7eb;
                    }
                    .code-box code { font-size: 1.5em; font-weight: bold; letter-spacing: 2px; }
                    .details { font-size: 0.8em; color: #666; }
                    @media print {
                        .no-print { display: none; }
                        body { padding: 0; background: white; }
                        .voucher-grid { gap: 20px; }
                    }
                </style>
            </head>
            <body>
                <div class="no-print" style="margin-bottom: 20px; text-align: right;">
                    <button onclick="window.print()" style="padding: 10px 20px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;">Print / Save as PDF</button>
                    <button onclick="window.close()" style="padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">Close</button>
                </div>
                <div class="voucher-grid">
                    ${vouchersHtml}
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(content);
        printWindow.document.close();
    };

    const searchedTerm = searchTerm.trim().toLowerCase();
    const visibleVouchers = searchedTerm
        ? vouchers.filter(v => [v.code, v.batchName, v.planId?.name, v.usedBy?.name, v.usedBy?.mobile]
            .filter(Boolean)
            .some(value => String(value).toLowerCase().includes(searchedTerm)))
        : vouchers;
    const batches = groupByBatch(visibleVouchers);

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-ticket-alt"></i>
                            Vouchers
                        </h1>
                        <p className="page-subtitle-modern">Generate and manage WiFi access vouchers</p>
                    </div>
                    {canCreate && (
                        <button className="btn-primary-modern" onClick={() => {
                            setShowModal(true);
                            if (formData.branchId) fetchPlans(formData.branchId);
                        }}>
                            <i className="fas fa-plus-circle"></i> Generate Vouchers
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Overview */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <i className="fas fa-ticket-alt"></i>
                    </div>
                    <div className="stat-details">
                        <span className="stat-value">{vouchers.length}</span>
                        <span className="stat-label">Total Vouchers</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon green">
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="stat-details">
                        <span className="stat-value">{vouchers.filter(v => v.status === 'active').length}</span>
                        <span className="stat-label">Active</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orange">
                        <i className="fas fa-history"></i>
                    </div>
                    <div className="stat-details">
                        <span className="stat-value">{vouchers.filter(v => v.status === 'used').length}</span>
                        <span className="stat-label">Used</span>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Fetching vouchers and batches...</p>
                </div>
            ) : vouchers.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className="fas fa-ticket-alt"></i>
                    </div>
                    <h3>No Vouchers Generated</h3>
                    <p>Create your first batch of vouchers to get started.</p>
                </div>
            ) : (
                <>
                <div className="table-wrapper-modern" style={{ marginBottom: '1.5rem' }}>
                    <div className="table-search-bar" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', borderBottom: 'none' }}>
                        <div className="search-box-modern">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search by access code, plan, or user..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="used">Used</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>
                </div>

                {batches.length === 0 ? (
                    <div className="empty-state-modern">
                        <div className="empty-icon">
                            <i className="fas fa-search"></i>
                        </div>
                        <h3>No Matches</h3>
                        <p>{searchTerm ? `No voucher matches "${searchTerm}"` : 'No vouchers match the selected filters'}</p>
                    </div>
                ) : (
                <div className="batches-list">
                    {batches.map(batch => (
                        <div key={batch.batchId} className="resource-card-modern mb-6" style={{ marginBottom: '1.5rem' }}>
                            <div className="card-header" style={{ paddingBottom: '1rem', borderBottom: 'none' }}>
                                <div className="resource-avatar" style={{ background: '#f3f4f6', color: '#4b5563' }}>
                                    <i className="fas fa-layer-group"></i>
                                </div>
                                <div className="resource-title">
                                    <h3>{batch.batchName || 'Unnamed Batch'}</h3>
                                    <span className="resource-subtitle">ID: {batch.batchId.substring(0, 12)}... • Created on {formatDate(batch.vouchers[0].createdAt)}</span>
                                </div>
                                <div className="status-badges">
                                    <span className="status-badge active" style={{ background: '#e0e7ff', color: '#3730a3' }}>
                                        <span className="badge-dot" style={{ background: '#4f46e5' }}></span>
                                        {batch.total} Total
                                    </span>
                                    <span className="status-badge active">
                                        <span className="badge-dot"></span>
                                        {batch.active} Available
                                    </span>
                                </div>
                                <div className="card-actions ml-auto">
                                    <button
                                        className="btn-secondary-modern btn-sm"
                                        onClick={() => handlePrintBatch(batch)}
                                        title="Print Batch"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
                                    >
                                        <i className="fas fa-print"></i> Print Batch
                                    </button>
                                </div>
                            </div>

                            <div className="p-0">
                                <table className="table-modern">
                                    <thead>
                                        <tr>
                                            <th>Access Code</th>
                                            <th>Plan</th>
                                            <th>Status</th>
                                            <th>Devices</th>
                                            <th>Validity</th>
                                            <th>User / Ref</th>
                                            <th className="text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batch.vouchers.slice(0, 10).map(voucher => (
                                            <tr key={voucher._id}>
                                                <td>
                                                    <div className="code-display" title="Click to copy" onClick={() => copyToClipboard(voucher.code)}>
                                                        <i className="fas fa-copy mr-2 opacity-50"></i>
                                                        <code>{voucher.code}</code>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="badge-detail">
                                                        <span className="font-semibold text-gray-800">{voucher.planId?.name || 'N/A'}</span>
                                                        <span className="text-xs text-gray-500">{planSummary(voucher.planId) || voucher.planId?.type || 'Standard'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${voucher.status === 'active' ? 'active' : voucher.status === 'used' ? 'suspended' : 'inactive'}`}>
                                                        <span className="badge-dot"></span>
                                                        {voucher.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    {(voucher.maxDevices || 1) > 1 ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                            <span style={{ fontWeight: 600, color: '#7c3aed', fontSize: '0.85rem' }}>
                                                                {voucher.deviceCount || 0}/{voucher.maxDevices}
                                                            </span>
                                                            <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', width: '60px' }}>
                                                                <div style={{ height: '100%', borderRadius: '2px', background: '#7c3aed', width: `${Math.min(100, ((voucher.deviceCount || 0) / voucher.maxDevices) * 100)}%` }} />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>1 device</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {voucher.validTill ? (
                                                        <div className="text-sm">
                                                            <div>{formatDate(voucher.validTill)}</div>
                                                            <div className="text-xs text-gray-500">Expires</div>
                                                        </div>
                                                    ) : <span className="text-gray-500 text-sm">No Expiry</span>}
                                                </td>
                                                <td>
                                                    {voucher.usedBy ? (
                                                        <div className="text-sm">
                                                            <div className="font-medium">{voucher.usedBy.name || 'User'}</div>
                                                            <div className="text-xs text-gray-500">{voucher.usedBy.mobile}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm italic">Unused</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="flex justify-end gap-2">
                                                        {canDelete && voucher.status !== 'used' && (
                                                            <button
                                                                className="action-btn-icon delete"
                                                                title="Revoke Voucher"
                                                                onClick={() => handleDelete(voucher._id)}
                                                                style={{ width: '32px', height: '32px' }}
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        )}
                                                        <button
                                                            className="action-btn-icon edit"
                                                            title="Print"
                                                            onClick={() => handlePrintBatch({
                                                                batchName: 'Single Voucher',
                                                                vouchers: [voucher]
                                                            })}
                                                            style={{ width: '32px', height: '32px' }}
                                                        >
                                                            <i className="fas fa-print"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {batch.vouchers.length > 10 && (
                                <div className="batch-pagination">
                                    <button className="btn-ghost">
                                        View all {batch.vouchers.length} vouchers in this batch
                                        <i className="fas fa-chevron-right ml-2"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                )}
                </>
            )}

            {/* Generate Modal */}
            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>Generate Vouchers</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleGenerate}>
                            <div className="modal-body-modern">

                                {/* Mode toggle */}
                                <div className="voucher-mode-toggle">
                                    <button
                                        type="button"
                                        className={`mode-btn ${formData.mode === 'batch' ? 'active' : ''}`}
                                        onClick={() => setFormData(f => ({ ...f, mode: 'batch' }))}
                                    >
                                        <i className="fas fa-layer-group"></i>
                                        <span>Batch</span>
                                        <small>Many codes, 1 device each</small>
                                    </button>
                                    <button
                                        type="button"
                                        className={`mode-btn ${formData.mode === 'multi' ? 'active' : ''}`}
                                        onClick={() => setFormData(f => ({ ...f, mode: 'multi' }))}
                                    >
                                        <i className="fas fa-devices"></i>
                                        <span>Multi-Device</span>
                                        <small>1 code, shared by N devices</small>
                                    </button>
                                </div>

                                <div className="form-section">
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label>Branch <span className="text-red-600">*</span></label>
                                            <select name="branchId" value={formData.branchId} onChange={handleInputChange} required>
                                                <option value="">Select Branch</option>
                                                {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                            </select>
                                        </div>

                                        <div className="form-group-modern">
                                            <label>Plan <span className="text-red-600">*</span></label>
                                            <select name="planId" value={formData.planId} onChange={handleInputChange} required disabled={!formData.branchId}>
                                                <option value="">Select Plan</option>
                                                {plans.map(p => <option key={p._id} value={p._id}>{[p.name, planSummary(p)].filter(Boolean).join(' — ')}</option>)}
                                            </select>
                                        </div>

                                        <div className="form-group-modern full-width">
                                            <label>{formData.mode === 'multi' ? 'Voucher Name / Label' : 'Batch Name'} <span className="text-red-600">*</span></label>
                                            <input
                                                type="text"
                                                name="batchName"
                                                value={formData.batchName}
                                                onChange={handleInputChange}
                                                placeholder={formData.mode === 'multi' ? 'e.g., Conference Hall WiFi' : 'e.g., Event Vouchers - Nov 2024'}
                                                required
                                            />
                                        </div>

                                        {formData.mode === 'batch' && (
                                            <div className="form-group-modern">
                                                <label>Quantity <span className="text-red-600">*</span></label>
                                                <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange} min="1" max="1000" required />
                                                <small className="text-gray-500 mt-1">Max: 1000 vouchers</small>
                                            </div>
                                        )}

                                        {formData.mode === 'multi' && (
                                            <div className="form-group-modern">
                                                <label>Max Devices <span className="text-red-600">*</span></label>
                                                <input
                                                    type="number"
                                                    name="maxDevices"
                                                    value={formData.maxDevices}
                                                    onChange={handleInputChange}
                                                    min="2"
                                                    max="500"
                                                    required
                                                />
                                                <small className="text-gray-500 mt-1">How many devices can use this code</small>
                                            </div>
                                        )}

                                        <div className="form-group-modern">
                                            <label>Custom Code (Optional)</label>
                                            <input
                                                type="text"
                                                name="customCode"
                                                value={formData.customCode}
                                                onChange={handleInputChange}
                                                placeholder={formData.mode === 'batch' ? 'e.g., AVINV2 → AVINV2001, AVINV2002…' : 'e.g., AVINV2'}
                                                style={{ textTransform: 'uppercase' }}
                                                maxLength={6}
                                            />
                                            <small className="text-gray-500 mt-1">
                                                {formData.customCode
                                                    ? formData.mode === 'batch' && parseInt(formData.quantity) > 1
                                                        ? `Codes: ${formData.customCode.toUpperCase()}001 … ${formData.customCode.toUpperCase()}${String(formData.quantity).padStart(3, '0')}`
                                                        : `Code: ${formData.customCode.toUpperCase()}`
                                                    : 'Leave blank to auto-generate random codes'}
                                            </small>
                                        </div>

                                        {!formData.customCode && (
                                        <div className="form-group-modern">
                                            <label>Code Length</label>
                                            <select name="codeLength" value={formData.codeLength} onChange={handleInputChange}>
                                                <option value="6">6 characters</option>
                                                <option value="8">8 characters</option>
                                                <option value="10">10 characters</option>
                                                <option value="12">12 characters</option>
                                            </select>
                                        </div>
                                        )}

                                        <div className="form-group-modern full-width">
                                            <label>Valid Until (Optional)</label>
                                            <DateInput
                                                type="date"
                                                name="validTill"
                                                value={formData.validTill}
                                                onChange={handleInputChange}
                                                min={toInputDate()}
                                            />
                                        </div>

                                    </div>

                                    <div className={`voucher-preview-box ${formData.mode === 'multi' ? 'multi' : ''}`}>
                                        <i className="fas fa-info-circle"></i>
                                        {formData.mode === 'multi' ? (
                                            <div>
                                                <strong>Multi-Device Voucher:</strong> Generates <strong>1 shared code</strong>{formData.customCode ? <> with code <strong>{formData.customCode.toUpperCase()}</strong></> : ''} that up to <strong>{formData.maxDevices || '?'} devices</strong> can connect with simultaneously.
                                            </div>
                                        ) : (
                                            <div>
                                                <strong>Batch:</strong> Will generate <strong>{formData.quantity}</strong> individual codes
                                                {formData.customCode
                                                    ? <> with prefix <strong>{formData.customCode.toUpperCase()}</strong> (e.g., {formData.customCode.toUpperCase()}001)</>
                                                    : <> with <strong>{formData.codeLength}</strong> random characters each</>
                                                }. Each code is one-time use for one device.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern">
                                    <i className="fas fa-magic"></i>
                                    {formData.mode === 'multi' ? 'Create Multi-Device Code' : 'Generate Vouchers'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <div>
                                <h2>Revoke Voucher</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="warning-box" style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#b91c1c', marginBottom: '0.5rem' }}>
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <strong>Warning</strong>
                                </div>
                                <p>Are you sure you want to revoke this voucher?</p>
                                <p style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: '0.5rem' }}>
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-ban"></i>
                                Revoke
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vouchers;
